var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { PreviewConsentFlow, PreviewConsentMode, PreviewMetadata, PreviewParticipantType, PreviewRequestType, } from "healthex-common/preview.dtos";
import { StudyTranslationLanguage } from "healthex-common/study.dtos";
import { pick, toTitleCase, toTitleCaseWithSpaces, willNeverHappen } from "healthex-common/utils";
import { computed } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { FormModel } from "./FormModel";
import { languageDescription } from "./LanguageDescriptions";
import { PatientConsentFlow } from "./PatientConsentUIManager";
const t = getObservableT("patients");
export class PatientConsentPreviewDialogModel {
    constructor(container) {
        this.isOpening = false;
        this.formModel = new FormModel({
            language: StudyTranslationLanguage.en,
            consentMode: PreviewConsentMode.REMOTE,
            consentFlow: PreviewConsentFlow.PATIENT_DIRECTED_DATA_EXCHANGE,
            participantType: PreviewParticipantType.ADULT,
            requestType: PreviewRequestType.INITIAL_HEALTH_RECORD_ACCESS,
        }, []);
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.studyPDFPreviewUIManager = container.studyPDFPreviewUIManager;
        this.bannerUIManager = container.bannerUIManager;
        this.formModel.onInputUpdate = async (input) => {
            if (!this.isOpening) {
                await this.restart();
                const whatChangedDescription = this.whatChangedDescription(input);
                if (whatChangedDescription) {
                    this.bannerUIManager.displayBanner({
                        title: `Switched to ${whatChangedDescription}`,
                        text: "You're now previewing from the beginning. Some steps or content may be different.",
                        type: "INFO",
                        durationInMS: 5000,
                    });
                }
            }
        };
    }
    onOpen() {
        if (this.patientConsentUIManager.session.interactivePreviewMetadata) {
            this.isOpening = true;
            try {
                this.formModel.updateInput(pick(this.patientConsentUIManager.session.interactivePreviewMetadata, "consentMode", "consentFlow", "language", "requestType"));
            }
            finally {
                this.isOpening = false;
            }
        }
    }
    async restart() {
        if (this.patientConsentUIManager.session.studyId && this.patientConsentUIManager.studyVersionId) {
            await this.patientConsentUIManager.startInteractivePreview(this.patientConsentUIManager.session.studyId, this.patientConsentUIManager.studyVersionId, new PreviewMetadata({
                consentMode: this.formModel.input.consentMode,
                consentFlow: this.formModel.input.consentFlow,
                language: this.formModel.input.language,
                participantType: this.formModel.input.participantType,
                requestType: this.formModel.input.requestType,
            }));
        }
    }
    get currentPreviewRoute() {
        return this.patientConsentUIManager.session.currentInteractivePreviewRoute;
    }
    get currentPreviewFlow() {
        return this.patientConsentUIManager.patientConsentFlow;
    }
    get title() {
        if (!this.currentPreviewFlow)
            return false;
        return "Enrollment informed consent experience";
    }
    get restartButtonDisabled() {
        if (!this.currentPreviewRoute)
            return false;
        return ["PRESCREEN_WELCOME", "ENROLLMENT_WELCOME", "WELCOME"].includes(this.currentPreviewRoute);
    }
    get dialogTitle() {
        if (this.currentPreviewFlow === PatientConsentFlow.ENROLLMENT) {
            return t("Patient enrollment flow");
        }
        else if (this.currentPreviewFlow === null) {
            return null;
        }
        return willNeverHappen(this.currentPreviewFlow);
    }
    get textInfo() {
        switch (this.currentPreviewRoute) {
            case "ENROLLMENT_WELCOME":
                return {
                    title: "Welcome to the Project",
                    explanation: "Introduces the patient to the request.",
                };
            case "ENROLLMENT_REQUEST_DETAILS_POPUP":
                return {
                    title: "Request Details",
                    explanation: "Full details of the consent request, including purpose, requested data, and recipients.",
                };
            case "ENROLLMENT_LOGIN":
                return {
                    title: "Identity Verification",
                    explanation: this.patientConsentUIManager.isPDDAGeneralDataUse
                        ? "Outlines the steps to complete the request, beginning with identity verification through CLEAR."
                        : "Introduces the patient to the request before they verify their identity and securely access HealthEx.",
                };
            case "ENROLLMENT_BILL_OF_RIGHTS":
                return {
                    title: "California Patient Bill of Rights",
                    explanation: "Explains patient rights related to participating in research, as required by state law. Currently, this includes the California Patient Bill of Rights, shown only to CA residents.",
                };
            case "ENROLLMENT_STUDY_INFORMATION":
                return { title: "Project Information", explanation: "Provides an overall introduction to the study." };
            case "ENROLLMENT_CONTACT":
                return {
                    title: "Who to Contact",
                    explanation: "Provides patients with contact information of who they can to reach out to with questions.",
                };
            case "ENROLLMENT_BENEFITS":
                return { title: "Project Benefits", explanation: "Benefits to a patient if they participate in the study." };
            case "ENROLLMENT_RISKS":
                return { title: "Project Risks", explanation: "Potential risks to a patient if they participate in the study." };
            case "ENROLLMENT_CHOICE":
                return {
                    title: "Voluntary Participation, Alternatives and Procedure for withdrawal",
                    explanation: "Informs patients that participation is voluntary and offering alternatives to participation.",
                };
            case "ENROLLMENT_DATA_TYPES":
                return { title: "Data Types", explanation: "Lists the types of patient data the project will be accessing." };
            case "ENROLLMENT_DATA_SHARING":
                return {
                    title: "Future Data Use",
                    explanation: "Explains possible use of health records for future research outside of the study.",
                };
            case "ENROLLMENT_CUSTOM_SECTION_1":
            case "ENROLLMENT_CUSTOM_SECTION_2":
            case "ENROLLMENT_CUSTOM_SECTION_3":
            case "ENROLLMENT_CUSTOM_SECTION_4":
            case "ENROLLMENT_CUSTOM_SECTION_5":
            case "ENROLLMENT_CUSTOM_SECTION_6":
            case "ENROLLMENT_CUSTOM_SECTION_7":
            case "ENROLLMENT_CUSTOM_SECTION_8":
            case "ENROLLMENT_CUSTOM_SECTION_9":
            case "ENROLLMENT_CUSTOM_SECTION_10":
                return {
                    title: "Additional Information Section",
                    explanation: "Additional details for the patient.",
                };
            case "ENROLLMENT_SENSITIVE_DATA_CONFIRMATION":
                return {
                    title: "Sensitive Data Confirmation",
                    explanation: "Only shown for patients receiving care in OR/TX/MA. This wording is provided by HealthEx.",
                };
            case "ENROLLMENT_INFORMED_CONSENT":
            case "ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_IN":
            case "ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_OUT":
            case "ENROLLMENT_INFORMED_CONSENT_PREVIEW_IM_INTERESTED":
                return {
                    title: "Enrollment Informed Consent",
                    explanation: "Requesting consent for accessing the patient's health records.\nNote: No consent will be logged in preview mode.",
                };
            case "ENROLLMENT_REAUTHORIZATION":
                return {
                    title: "Identity re-authorization",
                    explanation: "The patient is required to reconfirm their identity before they sign the consent form to join the project.",
                };
            case "ENROLLMENT_HIPAA_AUTHORIZATION":
                return {
                    title: "HIPAA Authorization and Consent Statement",
                    explanation: "Authorization request to allow for disclosure of data to the entity conducting the project.\nNote: No authorization will be granted in preview mode.",
                };
            case "ENROLLMENT_CONFIRMED":
                return {
                    title: "Thank You Message - Consent Granted for Enrollment",
                    explanation: "Thanks the patient for their time reviewing the request. This is only shown to patients who say yes.",
                };
            case "ENROLLMENT_DECLINED":
                return {
                    title: "Thank You Message - No Consent Granted for Enrollment",
                    explanation: "Thanks the patient for their time reviewing the request. This is only shown to patients who say no.",
                };
            case "ENROLLMENT_IM_INTERESTED_THANKYOU":
                return {
                    title: "Thank You Message - Needs follow-up",
                    explanation: "Thanks the patient for their time reviewing the request. This is only shown to patients who indicate they need a follow-up to learn more about the request.",
                };
            case "ENROLLMENT_EXCLUDED":
            case "ENROLLMENT_WELCOME_BACK":
                return { title: "Ineligible", explanation: "Ineligible for project." };
            case "ENROLLMENT_PURPOSE_OF_RESEARCH":
                return {
                    title: "Purpose of Research",
                    explanation: "Explains why is the study being done and a statement that the study involves research.",
                };
            case "ENROLLMENT_LENGTH_OF_RESEARCH":
                return {
                    title: "Length of Project and Number of Participants",
                    explanation: "Outlines when the study will begin and end, as well as the target number of patients to recruit.",
                };
            case "ENROLLMENT_HEALTH_RECORD_UPDATES":
                return {
                    title: "Health Record Updates",
                    explanation: "Informs the patient if their health records may need periodic updates to maintain accuracy, and may require them to take an action.",
                };
            case "ENROLLMENT_PROCEDURES_AND_INTERVENTIONS":
                return {
                    title: "Procedures and Interventions",
                    explanation: "Outlines the procedures and/or interventions the patient may experience if they participate in the study.",
                };
            case "ENROLLMENT_PARTICIPATION_AND_ACTIVITIES":
                return {
                    title: "Participation and Activities",
                    explanation: "Outlines the activities and/or procedures the patient may experience if they participate in the study.",
                };
            case "ENROLLMENT_TIMELINE_OF_VISITS":
                return {
                    title: "Timeline for Visits and Interventions",
                    explanation: "Provides a schedule for when visits and/or interventions would take place.",
                };
            case "ENROLLMENT_CONFIDENTIALITY_OF_DATA":
                return {
                    title: "Confidentiality Measures",
                    explanation: "Explains how confidentiality will be ensured and health records will be managed during the study.",
                };
            case "ENROLLMENT_INJURY_PROTOCOL":
                return { title: "Injury Protocol", explanation: "Explains the process if patient injury occurs from the study." };
            case "ENROLLMENT_PARTICIPATION_TERMINATION":
                return {
                    title: "Participation Termination",
                    explanation: "Explains reasons why participation may be terminated by the investigator.",
                };
            case "ENROLLMENT_LEAVING_STUDY_EARLY":
                return {
                    title: "Withdrawal Implications",
                    explanation: "Explains the consequences or process required if the patient leaves the study before it’s completed.",
                };
            case "ENROLLMENT_COST_OF_PARTICIPATION":
                return {
                    title: "Cost of Participation",
                    explanation: "Details if there are any costs the patient may incur if they participate in the study.",
                };
            case "ENROLLMENT_SHARING_RESULTS":
                return {
                    title: "Research Results",
                    explanation: "Explains if and how the results of the research will be shared with patients.",
                };
            case "ENROLLMENT_PATIENT_COMPENSATION":
                return {
                    title: "Patient Compensation",
                    explanation: "Details if any compensation will provided to the patient if they participate in the study.",
                };
            case "ENROLLMENT_PDDA_RECORD_LOCATION":
                return {
                    title: "Record Locator",
                    explanation: "Shows a list of the patient's places of care and the associated health records retrieved.",
                };
            case "ENROLLMENT_PARTICIPATION_SIGNATURE":
                return {
                    title: "Participant Signature",
                    explanation: "Obtains the patient's signature to confirm their understanding and consent to grant access to their health records.\nNote: No consent will be logged in preview mode.",
                };
            case "ENROLLMENT_PASS_DEVICE_TO_PARENT_GUARDIAN":
                return {
                    title: "Pass Device to Parent/Guardian",
                    explanation: "If applicable, this screen prompts the participant or the other parent/guardian to hand over the device so they can provide their consent.",
                };
            case "ENROLLMENT_PARENT_GUARDIAN_REAUTHORIZATION":
                return {
                    title: "Parent/Guardian Authentication",
                    explanation: "If the parent or guardian did not initially authenticate, this screen will prompt them to authenticate before signing the consent.",
                };
            case "ENROLLMENT_PARENT_GUARDIAN_SIGNATURE":
                return {
                    title: "Parent/Guardian Signature",
                    explanation: "This is only shown if the patient is a minor. Obtains the consent for a patient who is a minor to grant access to their health records.\nNote: No consent will be logged in preview mode.",
                };
            case "ENROLLMENT_PASS_DEVICE_TO_ADDITIONAL_PARENT_GUARDIAN":
                return {
                    title: "Pass Device to Additional Parent/Guardian",
                    explanation: "If applicable, this screen prompts the participant or the other parent/guardian to hand over the device so they can provide their consent.",
                };
            case "ENROLLMENT_ADDITIONAL_PARENT_GUARDIAN_REAUTHORIZATION":
                return {
                    title: "Additional Parent/Guardian Authentication",
                    explanation: "If the consent requires both parent or guardian signatures,this screen will prompt the additional parent or guardian to authenticate before signing the consent.",
                };
            case "ENROLLMENT_ADDITIONAL_PARENT_GUARDIAN_SIGNATURE":
                return {
                    title: "Additional Parent/Guardian Signature",
                    explanation: "This is only shown if the patient is a minor. Obtains the consent for a patient who is a minor to grant access to their health records.\nNote: No consent will be logged in preview mode.",
                };
            case "ENROLLMENT_PASS_DEVICE_TO_MINOR":
                return {
                    title: "Pass Device to Minor",
                    explanation: "If the participant is a minor, this screen will prompt the parent or guardian to pass the device to the minor in order to have them provide assent.",
                };
            case "ENROLLMENT_MINOR_SIGNATURE":
                return { title: "Minor Assent", explanation: "This screen provides the way for minor to provide prescreen assent." };
            case "ENROLLMENT_PASS_DEVICE_TO_WITNESS":
                return {
                    title: "Pass Device to Witness",
                    explanation: "This screen notifies the user that, if required, the witness must be ready to sign the consent.",
                };
            case "ENROLLMENT_WITNESS_SIGNATURE":
                return {
                    title: "Witness Signature",
                    explanation: "This is only shown if the patient grants access to their health records through guided consent. Confirms the witness’s observation of the consent process, verifying that the patient signed voluntarily.\nNote: No consent will be logged in preview mode.",
                };
            case "ENROLLMENT_PASS_DEVICE_TO_PROVIDER":
                return {
                    title: "Pass Device to Provider",
                    explanation: "This screen instructs the user to hand the device to the provider to complete their consent signature.",
                };
            case "ENROLLMENT_PROVIDER_AUTHORIZATION": {
                return {
                    title: "Consent facilitator signature",
                    explanation: "This is only shown if the patient grants access to their health records through guided consent. Verifies the facilitation of the consent process and the facilitator's confirmation that the patient received the necessary information.\nNote: No consent will be logged in preview mode.",
                };
            }
            case "ENROLLMENT_DECLINED_ACCESS":
                return {
                    title: "Declined Access",
                    explanation: "This screen informs the patient that they have declined to share their records.",
                };
            case "ENROLLMENT_IDENTITY_VERIFIED":
                return { title: "Identity verified", explanation: "Page shown after the patient has verified their identity." };
            case "ENROLLMENT_PREREAD_THANKYOU":
            case "ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_IN_PARTICIPANT_CONSENT_INTERIM_PAGE":
            case "ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_IN_MINOR_CONSENT_INTERIM_PAGE":
            case "ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_OUT_INTERIM_PAGE":
            case "ENROLLMENT_INFORMED_CONSENT_PREVIEW_IM_INTERESTED_INTERIM_PAGE":
            case "ENROLLMENT_PARTICIPATION_SIGNATURE_SCREENSHOT_PREVIEW_DECLINED":
            case "ENROLLMENT_PDDA_RECORD_LOCATION_SCREENSHOT_REVIEW_AND_SHARE":
            case null:
                return { title: "", explanation: "" };
        }
        return willNeverHappen(this.currentPreviewRoute);
    }
    get languagesEnabled() {
        return this.studyPDFPreviewUIManager.generatableLanguages;
    }
    get minorEnabled() {
        return this.patientConsentUIManager.isMinorConsentEnabled;
    }
    get requestTypeVisible() {
        return this.studyPDFPreviewUIManager.includeRequestTypes;
    }
    get consentModeVisible() {
        return this.studyPDFPreviewUIManager.includeGuided;
    }
    get projectPreviewOptions() {
        return this.studyPDFPreviewUIManager.projectPreviewOptions;
    }
    get participantTypeVisible() {
        return !!this.projectPreviewOptions.participantType;
    }
    whatChangedDescription(input) {
        if (input.language) {
            return `${languageDescription(input.language)} language`;
        }
        else if (input.consentMode) {
            return `${toTitleCase(input.consentMode)} consent mode`;
        }
        else if (input.consentFlow) {
            return `${toTitleCase(input.consentFlow)} consent flow`;
        }
        else if (input.participantType) {
            return `${toTitleCase(input.participantType)} participant type`;
        }
        else if (input.requestType) {
            return `${toTitleCaseWithSpaces(input.requestType)} request type`;
        }
        else {
            return null;
        }
    }
    get showLoginGradient() {
        return this.patientConsentUIManager.showClassicLoginPage;
    }
}
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "currentPreviewRoute", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "currentPreviewFlow", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "title", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "restartButtonDisabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "dialogTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "textInfo", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "languagesEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "minorEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "requestTypeVisible", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "consentModeVisible", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "projectPreviewOptions", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "participantTypeVisible", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentPreviewDialogModel.prototype, "showLoginGradient", null);
